import { useShadcnNotification } from '@src/components/ui/notification';
import {
    PreviewDocument,
    PreviewDocumentRef,
} from '@src/features/DocumentSign/PreviewDocument';
import {
    SignFlow,
    SignFlowRef,
    SignFlowTriggerData,
    type MobileSignUrls,
} from '@src/features/DocumentSign/SignFlow';
import LayoutView from '@src/features/LayoutViewExpose';
import {
    ServiceButtonAction,
    useServiceBtnViewer,
} from '@src/features/useServiceBtnViewer';
import {
    ServiceFormDirtyObserver,
    ServiceFormDirtyObserverRef,
} from '@src/features/useServiceBtnViewer/ServiceFormDirtyObserver';
import {
    getHyperlinkOptions,
    HyperlinkOptions,
    openHyperlink,
} from '@src/features/useServiceBtnViewer/serviceHyperlink';
import {
    ServiceHyperlinkModal,
    ServiceHyperlinkModalRef,
} from '@src/features/useServiceBtnViewer/ServiceHyperlinkModal';
import {
    ReceptionScheduleSlotModal,
    type ReceptionScheduleSlotModalRef,
} from '@src/pages/Services/components/ReceptionScheduleSlotModal';
import { useEgovMobileSignCheckProgress } from '@src/pages/Services/hooks/useEgovmobileSignCheckProgress';

import { ShadcnAction } from '@shared/components/ShadcnAction';
import useLangText from '@shared/hooks/useLangText';
import { useQueryMutation } from '@shared/hooks/useQueryMutation';
import {
    MobileSignSessionStatus,
    ProcessFieldRaw,
} from '@shared/types/process';
import { isDeepEqual } from '@shared/util/isDeepEqual';
import { omitHiddenDependentLayoutFieldValues } from '@shared/util/layoutDependentFields';
import { makeTree } from '@shared/util/makeTree';
import { resumeMobileSign } from '@shared/util/resumeMobileSign';
import { scrollFormIntoView } from '@shared/util/scrollFormIntoView';
import {
    decideSignFromForm,
    formValuesAfterPreparationSaved,
    readSignPreparationFields,
} from '@shared/util/signPreparation';

import {
    applyServerValidationErrors,
    WebComponentServerValidationError,
} from './serverValidationErrors';

import { ShadcnForm } from '@shadcn-features/core';
import { getQueryKey } from '@shadcn-features/query';
import type { ShadcnNotificationArgs } from '@shadcn-features/query';
import { useQueryClient } from '@tanstack/react-query';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type SubmitAction =
    | (ServiceButtonAction & {
          extraValues?: Record<string, unknown>;
          extraFields?: Record<string, unknown>;
      })
    | {
          buttonId: string;
          extraValues?: Record<string, unknown>;
          extraFields?: Record<string, unknown>;
          hyperlinkOptions?: HyperlinkOptions | null;
      };

type LayoutViewHostElement = HTMLElement & {
    executeAction: (data: Record<string, unknown>) => Promise<void>;
    refreshService?: () => Promise<void>;
    submitAction?: (action: ServiceButtonAction) => Promise<void>;
    showPreview?: (documentId: string) => Promise<void>;
    setServerValidationErrors?: (
        errors: WebComponentServerValidationError[],
    ) => void;
    clearServerValidationErrors?: () => void;
};

const getLayoutViewHostElement = (
    element: HTMLElement | null,
): LayoutViewHostElement | null => {
    if (!element) return null;

    const rootNode = element.getRootNode();
    if (rootNode instanceof ShadowRoot) {
        return rootNode.host as LayoutViewHostElement;
    }

    return element.closest('layout-view') as LayoutViewHostElement | null;
};

type LayoutViewWebComponentsProps = Omit<
    React.ComponentPropsWithoutRef<typeof LayoutView>,
    'childrenBtnForFlowTab'
> & {
    mobileSignUrls?: MobileSignUrls;
    openUri?: (uri: string) => void;
    serverValidationErrors?: WebComponentServerValidationError[];
};

export const LayoutViewWebComponents = ({
    layoutProcess,
    service,
    tabProps,
    fileUploadController,
    layoutVariant,
    mobileSignUrls,
    openUri,
    serverValidationErrors,
}: LayoutViewWebComponentsProps) => {
    const { t } = useTranslation();
    const getLangText = useLangText();

    const refDiv = useRef<HTMLDivElement>(null);

    const [form, setForm] = useState<UseFormReturn>();
    const [defaultValues, setDefaultValues] = useState<Record<string, unknown>>(
        {},
    );
    const handleForm = useCallback((nextForm: UseFormReturn) => {
        setForm((currentForm) =>
            currentForm === nextForm ? currentForm : nextForm,
        );
    }, []);

    const signRef =
        useRef<SignFlowRef<ServiceButtonAction & SignFlowTriggerData>>(null);
    const previewDocRef = useRef<PreviewDocumentRef>(null);
    const hyperlinkModalRef =
        useRef<ServiceHyperlinkModalRef<ServiceButtonAction>>(null);
    const formDirtyObserverRef = useRef<ServiceFormDirtyObserverRef>(null);
    const receptionScheduleRef = useRef<ReceptionScheduleSlotModalRef>(null);

    const notification = useShadcnNotification();

    const { autoMakeBtns } = useServiceBtnViewer({
        buttons: service?.buttons ?? [],
        service: service,
    });

    const { removeMobileSignSessionId, mobileSignSessionId } =
        useEgovMobileSignCheckProgress({
            signRef: signRef,
            enabled: Boolean(service?.id),
        });
    const notifyReceptionBookingChanged = useCallback(() => {
        if (!service?.id) return;

        refDiv.current?.dispatchEvent(
            new CustomEvent('onReceptionBookingChanged', {
                detail: { serviceId: service.id },
                bubbles: true,
                composed: true,
            }),
        );
    }, [service?.id]);

    const layoutFieldIds = useMemo(() => {
        const fieldIds: string[] = [];
        layoutProcess.blocks?.forEach((block) => {
            if (block.type === 'Field') {
                fieldIds.push(block.options.processFieldId);
            }
        });

        return fieldIds;
    }, [layoutProcess.blocks]);

    const queryClient = useQueryClient();
    const serverErrorFieldNamesRef = useRef<string[]>([]);

    const getFieldLabel = useCallback(
        (fieldId: string) => {
            const cachedField = queryClient.getQueryData<ProcessFieldRaw>(
                getQueryKey({
                    url: `flow/ServiceProcessFields/Get/${fieldId}`,
                }),
            );

            return cachedField ? getLangText(cachedField.name) : undefined;
        },
        [getLangText, queryClient],
    );

    const validateForm = useCallback(async () => {
        const isValid = await form?.trigger();

        if (form && isValid) {
            return omitHiddenDependentLayoutFieldValues(
                form.getValues(),
                makeTree(layoutProcess.blocks ?? []),
            );
        } else {
            const firstErrorFieldId = layoutFieldIds.find(
                (fieldId) => form?.getFieldState(fieldId).error,
            );
            const firstErrorFieldLabel = firstErrorFieldId
                ? getFieldLabel(firstErrorFieldId)
                : null;

            notification.error({
                message: t('translation:validationRequired'),
                description:
                    firstErrorFieldLabel ??
                    t('translation:pleaseFillAllRequiredFields'),
            });
        }
    }, [
        form,
        getFieldLabel,
        layoutFieldIds,
        layoutProcess.blocks,
        notification,
        t,
    ]);

    const executeHostAction = useCallback(
        async (
            hostElement: LayoutViewHostElement,
            detail: Record<string, unknown>,
        ) => {
            const ancestors: HTMLElement[] = [];
            for (
                let node = hostElement.parentElement;
                node;
                node = node.parentElement
            ) {
                ancestors.push(node);
            }
            try {
                await hostElement.executeAction(detail);
                await hostElement.refreshService?.();
                requestAnimationFrame(() => {
                    // The portal can replace the entire WC with its final success screen.
                    const target =
                        refDiv.current ??
                        ancestors.find((node) => node.isConnected) ??
                        null;
                    scrollFormIntoView(target);
                });
            } catch (error) {
                // The host and WC have separate Sonner stores and overlay layers.
                const args = error as Partial<ShadcnNotificationArgs> | null;
                notification.error({
                    message: args?.message || t('translation:error'),
                    description: args?.description,
                });
                throw error;
            }
        },
        [notification, t],
    );

    const handelSubmit = useCallback(
        async ({
            extraValues,
            extraFields,
            hyperlinkOptions,
            ...action
        }: SubmitAction) => {
            if (serverErrorFieldNamesRef.current.length) {
                form?.clearErrors(serverErrorFieldNamesRef.current);
                serverErrorFieldNamesRef.current = [];
            }

            const preparedFields = readSignPreparationFields(extraValues);
            if (preparedFields && service?.id) {
                const {
                    signPreparationFields: _signPreparationFields,
                    ...executeExtras
                } = extraValues ?? {};
                const detail = {
                    serviceId: service.id,
                    ...executeExtras,
                    ...action,
                    fields: preparedFields,
                };
                const hostElement = getLayoutViewHostElement(refDiv.current);
                if (
                    !hostElement ||
                    typeof hostElement.executeAction !== 'function'
                ) {
                    notification.error({
                        message: t('translation:error'),
                    });
                    return false;
                }

                await executeHostAction(hostElement, detail);

                if (hyperlinkOptions && !openHyperlink(hyperlinkOptions)) {
                    notification.error({
                        message: t('translation:error'),
                    });
                }

                return true;
            }

            const data = await validateForm();

            if (data && service?.id) {
                const detail = {
                    fields: { ...data, ...extraFields },
                    serviceId: service.id,
                    ...extraValues,
                    ...action,
                };
                const hostElement = getLayoutViewHostElement(refDiv.current);
                if (
                    !hostElement ||
                    typeof hostElement.executeAction !== 'function'
                ) {
                    notification.error({
                        message: t('translation:error'),
                    });
                    return false;
                }

                await executeHostAction(hostElement, detail);

                if (hyperlinkOptions && !openHyperlink(hyperlinkOptions)) {
                    notification.error({
                        message: t('translation:error'),
                    });
                }

                return true;
            }

            return false;
        },
        [executeHostAction, form, notification, service?.id, t, validateForm],
    );

    const resumeStatusMutation = useQueryMutation<
        string,
        MobileSignSessionStatus
    >(
        mobileSignUrls?.status ?? 'flow/Account/MobileSignSessions/Status/{id}',
        'get',
        {
            variablesOption(url, variables, axiosConfig) {
                return { url: url.replace('{id}', variables), axiosConfig };
            },
            hideErrorNotification: true,
            hideSuccessNotification: true,
        },
    );

    const handleResumeMobileSign = useCallback(
        async (status: MobileSignSessionStatus, sessionId: string) => {
            const host = getLayoutViewHostElement(refDiv.current);
            if (
                !service?.id ||
                mobileSignSessionId !== sessionId ||
                typeof host?.executeAction !== 'function'
            ) {
                throw new Error('mobile sign resume data not found');
            }
            await resumeMobileSign({
                status,
                sessionId,
                execute: async () => {
                    if (
                        status.status !== 'Completed' ||
                        !status.buttonId ||
                        !status.cms
                    ) {
                        throw new Error('mobile sign resume data not found');
                    }
                    await executeHostAction(host, {
                        serviceId: service.id,
                        buttonId: status.buttonId,
                        mobileSignSessionId: sessionId,
                        serviceDocumentId: status.serviceDocumentId,
                        signData: status.cms,
                    });
                },
                getStatus: () => resumeStatusMutation.mutateAsync(sessionId),
                refresh: async () => {
                    if (typeof host.refreshService !== 'function') {
                        throw new Error(
                            'mobile sign resume refresh not available',
                        );
                    }
                    await host.refreshService();
                },
            });
        },
        [
            executeHostAction,
            mobileSignSessionId,
            resumeStatusMutation,
            service?.id,
        ],
    );

    const continueAction = useCallback(
        async (action: ServiceButtonAction) => {
            if (
                action.actionCode &&
                action.buttonId &&
                action.isSign &&
                !action.type &&
                (action.signDocumentId || action.needsSignPreparation)
            ) {
                const validated = action.needsSignPreparation
                    ? await validateForm()
                    : undefined;
                const decision = decideSignFromForm({
                    needsSignPreparation: Boolean(action.needsSignPreparation),
                    validatedFields: validated,
                });

                if (decision.type === 'invalid') return true;

                signRef.current?.trigger(
                    {
                        ...action,
                        serviceId: service?.id,
                        ...(decision.type === 'prepare'
                            ? { signPreparationFields: decision.fields }
                            : {}),
                    },
                    action.signDocumentId,
                    {
                        mobileSignIntent:
                            action.buttonType === 'Approve'
                                ? 'ApprovalDecisionSign'
                                : 'ServiceDocumentSign',
                    },
                );
                return true;
            }

            return handelSubmit(action);
        },
        [handelSubmit, service?.id, validateForm],
    );

    const handleAction = useCallback(
        async (action: ServiceButtonAction) => {
            const isReceptionScheduleBtn = action.buttonActions?.some(
                (buttonAction) =>
                    buttonAction.actionCode === 'ReceptionSchedule',
            );
            const isActiveBooking = service?.receptionSchedules?.some(
                (item) => item.booking?.status === 'Held',
            );
            const isExpiredBooking = service?.receptionSchedules?.some(
                (item) => item.booking?.status === 'Released',
            );
            if (isExpiredBooking && !isReceptionScheduleBtn) {
                notification.error({
                    message: t('translation:receptionScheduleExpiredTitle'),
                    description: t(
                        'translation:receptionScheduleExpiredDescription',
                    ),
                });
                return;
            }
            const normalizedAction = {
                ...action,
                hyperlinkOptions:
                    action.hyperlinkOptions ??
                    getHyperlinkOptions(action.buttonActions),
            };

            if (
                normalizedAction.hyperlinkOptions?.openMode === 'CurrentTab' &&
                formDirtyObserverRef.current?.isDirty
            ) {
                hyperlinkModalRef.current?.open(normalizedAction);
                return;
            }

            if (
                'buttonActions' in action &&
                isReceptionScheduleBtn &&
                !isActiveBooking &&
                action.buttonId
            ) {
                const schedule = service?.receptionSchedules?.find(
                    (item) => item.buttonId === action.buttonId,
                );
                receptionScheduleRef.current?.open(
                    action.buttonId,
                    () => {
                        void continueAction(normalizedAction);
                    },
                    schedule?.organizationId,
                );
                return;
            }

            await continueAction(normalizedAction);
        },
        [continueAction, service, t, notification],
    );

    useEffect(() => {
        const hostElement = getLayoutViewHostElement(refDiv.current);

        if (!hostElement) return;

        hostElement.submitAction = async (action) => {
            await handleAction(action);
        };
        hostElement.showPreview = async (documentId) => {
            if (!documentId) return;
            previewDocRef.current?.trigger(documentId);
        };
        hostElement.setServerValidationErrors = (errors) => {
            if (!form) return;

            if (serverErrorFieldNamesRef.current.length) {
                form.clearErrors(serverErrorFieldNamesRef.current);
            }

            serverErrorFieldNamesRef.current = applyServerValidationErrors({
                errors,
                form,
                getFieldLabel,
                notification,
                t,
            });
        };
        hostElement.clearServerValidationErrors = () => {
            if (!form || serverErrorFieldNamesRef.current.length === 0) return;
            form.clearErrors(serverErrorFieldNamesRef.current);
            serverErrorFieldNamesRef.current = [];
        };
    }, [form, getFieldLabel, handleAction, notification, t, validateForm]);

    useEffect(() => {
        if (!form) return;

        if (serverErrorFieldNamesRef.current.length) {
            form.clearErrors(serverErrorFieldNamesRef.current);
            serverErrorFieldNamesRef.current = [];
        }

        if (!serverValidationErrors?.length) return;

        serverErrorFieldNamesRef.current = applyServerValidationErrors({
            errors: serverValidationErrors,
            form,
            getFieldLabel,
            notification,
            t,
        });
    }, [form, getFieldLabel, notification, serverValidationErrors, t]);

    useEffect(() => {
        if (!form) return;
        if (
            service?.fieldValues &&
            !isDeepEqual(service.fieldValues, defaultValues)
        ) {
            setDefaultValues(service.fieldValues);
            form.reset(service.fieldValues);
        }
    }, [defaultValues, form, service?.fieldValues]);

    return (
        <div ref={refDiv}>
            <ShadcnForm
                mode='onChange'
                key={service?.currentStageId}
                onForm={handleForm}
                defaultValues={service?.fieldValues}
            >
                <ServiceFormDirtyObserver ref={formDirtyObserverRef} />
                <LayoutView
                    fileUploadController={fileUploadController}
                    layoutProcess={layoutProcess}
                    layoutVariant={layoutVariant}
                    service={service}
                    tabProps={tabProps}
                    classNameWrapper='gap-y-6'
                    childrenBtnForFlowTab={
                        <ShadcnAction
                            items={autoMakeBtns}
                            wrapper={{
                                wrapperType: 'flowPopover',
                                text: t('translation:actions'),
                                buttonProps: {
                                    variant: 'brandGreen',
                                    size: 'sm',
                                },
                            }}
                            onAction={async (action) => {
                                await handleAction(action);
                            }}
                        />
                    }
                />
            </ShadcnForm>
            <PreviewDocument ref={previewDocRef} />
            <ServiceHyperlinkModal
                ref={hyperlinkModalRef}
                onContinue={continueAction}
            />
            {service?.id ? (
                <ReceptionScheduleSlotModal
                    key={service.id}
                    ref={receptionScheduleRef}
                    serviceId={service.id}
                    onBookingChanged={notifyReceptionBookingChanged}
                />
            ) : null}
            <SignFlow
                ref={signRef}
                mobileSignServiceId={service?.id}
                mobileSignUrls={mobileSignUrls}
                openUri={openUri}
                servicesController={fileUploadController ?? 'flow/Services'}
                removeMobileSignSessionId={removeMobileSignSessionId}
                onResumeMobileSign={handleResumeMobileSign}
                onSignPreparationSaved={(fields) => {
                    if (!form) return;
                    form.reset(
                        formValuesAfterPreparationSaved(
                            form.getValues(),
                            fields,
                        ),
                    );
                }}
                onAfterSign={async (extraData) => {
                    const { hyperlinkOptions, ...extraValues } =
                        extraData ?? {};
                    const data = {
                        ...extraValues,
                        buttonId: extraData?.buttonId ?? '',
                        hyperlinkOptions,
                        extraValues,
                    };
                    await handelSubmit(data);
                }}
            />
        </div>
    );
};
